<template>
  <ViewHeader
    v-if="!categoriesApi.loading.value"
    :title="matchedItem?.title"
    :dynamic-breadcrumbs="breadcrumbs"
  />

  <Block v-if="categoriesApi.loading.value">
    <Spinner color="primary" size="large" />
  </Block>
  <Block v-else-if="error">
    <Notification type="error" :text="$t(error)">
      <template #actions>
        <Button
          :text="$t('tryAgain')"
          @click="getCategories"
          size="small"
          color="error"
        >
          <template #icon>
            <i-mdi-refresh />
          </template>
        </Button>
      </template>
    </Notification>
  </Block>
  <Block v-else-if="subnavItems.length">
    <List>
      <ListItem
        v-for="item of subnavItems"
        :key="item.id"
        :text="item.title"
        :footer="$t('numStations', item.num_stations)"
        :to="{ path: `${router.currentRoute.value.path}/${item.name}` }"
      >
        <template #icon>
          <i-mdi-cube />
        </template>
      </ListItem>
    </List>
  </Block>
  <Block v-else-if="matchedItem">
    <SearchResults :category="matchedItem.name" />
  </Block>
</template>

<script setup lang="ts">
import type { Category, CategoriesResponse } from '@/types'
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApi } from '@/lib/api'
import { useDynamicRoute } from '@/lib/dynamic-route'

const route = useRoute()
const router = useRouter()

const categoriesApi = useApi()
const categories = ref<Category[]>([])
const error = ref<string | undefined>(undefined)

const { matchedItem, breadcrumbs } = useDynamicRoute({
  level: 3,
  _children: categories,
  onSlugMismatch: () => {
    router.replace({ name: 'error404' })
  },
})

const subnavItems = computed<Category[]>(() => {
  if (route.name === 'categoryContainer') {
    return categories.value
  }

  if (matchedItem.value) {
    return matchedItem.value._children || []
  }

  return []
})

const getCategories = async () => {
  try {
    error.value = undefined

    const response = await categoriesApi.ky
      .get('categories/')
      .json<CategoriesResponse>()

    if (!response?.data.length) {
      error.value = 'categories.resolveError'
      return
    }

    categories.value = response.data
  } catch (_e) {
    error.value = 'categories.resolveError'
    categories.value = []
  }
}

onMounted(() => {
  getCategories()
})
</script>
